import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import {
  agentConnectorConfigsKeys,
  agentTokenKeys,
  externalAPIKeys,
} from "../../../api/queryKeys";
import ContainedButton from "../../../components/buttons/ContainedButton";
import { StyledTextField } from "../../../components/forms/styles/formFieldStyles";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import CodeBlockWithCopy from "./CodeBlockWithCopy";
import OnPremCollapseSection from "./OnPremCollapseSection";

export default function InstallationGuideSection({ selectedVersion = "" }) {
  const colors = useColors();
  const [codeLines, setCodeLines] = useState("");
  const [imageUri, setImageUri] = useState("");
  const [isTokenGenerated, setIsTokenGenerated] = useState(false);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);

  const { getBreezeBackendURL, getAgentConnectorConfigs, getAgentToken } =
    useAPI();

  const { data: backendURL, isLoading: isLoadingBackendURL } = useQuery({
    queryKey: externalAPIKeys.backendURL(),
    queryFn: getBreezeBackendURL,
    select: (response) => response.data,
  });

  const {
    data: configArtifacts = [],
    isSuccess: isConfigsLoaded,
    isLoading: isLoadingConfigs,
  } = useQuery({
    queryKey: agentConnectorConfigsKeys.all,
    queryFn: async () => {
      const { data } = await getAgentConnectorConfigs();
      return Array.isArray(data) ? data : [];
    },
  });

  const {
    data: agentToken,
    isLoading: isLoadingAgentToken,
    refetch: refetchAgentToken,
  } = useQuery({
    queryKey: agentTokenKeys.all,
    queryFn: () =>
      getAgentToken()
        .then((response) => {
          setIsTokenGenerated(true);
          Toast.success("Breeze token generated successfully");
          setIsGeneratingToken(false);
          return response;
        })
        .catch((error) => {
          Toast.error("Failed to generate Breeze token");
          setIsGeneratingToken(false);
          throw error;
        }),
    select: (response) => response.data,

    retry: false,
    enabled: false,
  });

  const handleGenerateAgentToken = () => {
    setIsGeneratingToken(true);
    refetchAgentToken();
  };

  useEffect(() => {
    const hasBackend = !isLoadingBackendURL && !!backendURL;
    const preferredConfig =
      (Array.isArray(configArtifacts) &&
        (configArtifacts.find((a) => a.version === selectedVersion) ||
          configArtifacts[0])) ||
      null;
    const configName = preferredConfig?.configName || "";
    const version = preferredConfig?.version || "";
    const fileType = (preferredConfig?.fileType || "").toLowerCase();
    const hasConfig = !!configName && !!version && !!fileType;

    if (hasBackend && hasConfig) {
      const chartFileName = `${configName}-${version}.${fileType}`;
      const resolvedImageUri = (imageUri || "").trim() || "<IMAGE_URI>";
      const resolvedAgentToken =
        isTokenGenerated && !isGeneratingToken
          ? (agentToken || "").trim()
          : "<BREEZE_TOKEN>";

      const envLines = [
        `BREEZE_BACKEND_URL="${backendURL}"`,
        `BREEZE_IMAGE_URI="${resolvedImageUri}"`,
        `BREEZE_TOKEN="${resolvedAgentToken}"`,
      ];

      setCodeLines(
        [
          ...envLines,
          "helm upgrade --install breeze-connector --set image.uri=$" +
            "{BREEZE_IMAGE_URI} --set config.backend_url=$" +
            "{BREEZE_BACKEND_URL} --set secret.data.BREEZE_TOKEN=$" +
            "{BREEZE_TOKEN} ./" +
            chartFileName,
        ].join("\n")
      );
    }
  }, [
    isLoadingBackendURL,
    backendURL,
    configArtifacts,
    isConfigsLoaded,
    selectedVersion,
    imageUri,
    agentToken,
    isTokenGenerated,
    isGeneratingToken,
  ]);

  return (
    <OnPremCollapseSection title="Installation Guide" defaultCollapsed={false}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pl: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxWidth: 600,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text_secondary }}>
              Image URI
            </Typography>
            <StyledTextField
              size="small"
              placeholder="e.g., public.ecr.aws/q7r5t8d6/breeze/agent:0.1.0"
              value={imageUri}
              onChange={(e) => setImageUri(e.target.value)}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" sx={{ color: colors.text_secondary }}>
              Breeze Token
            </Typography>
            <ContainedButton
              color="primary"
              size="small"
              disabled={isLoadingAgentToken || isGeneratingToken}
              onClick={handleGenerateAgentToken}
            >
              {isGeneratingToken ? (
                <Box
                  sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
                >
                  <LoadingIcon
                    size={14}
                    sx={{ color: colors.button_contained_disabled_text }}
                  />
                  Generating...
                </Box>
              ) : (
                "Generate"
              )}
            </ContainedButton>
          </Box>
        </Box>
        <Box sx={{ position: "relative", width: "100%", maxWidth: "100%" }}>
          {isLoadingBackendURL || isLoadingConfigs || !codeLines ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: colors.grey_200,
                padding: 2,
                borderRadius: 1,
                fontSize: "0.8rem",
                lineHeight: 1.5,
                fontFamily: "monospace",
                margin: 0,
                maxWidth: "100%",
                minHeight: "64px",
              }}
            >
              <LoadingIcon size={16} />
            </Box>
          ) : (
            <CodeBlockWithCopy
              code={codeLines}
              eventName="User copied agent connector installation guide to clipboard"
            />
          )}
        </Box>
      </Box>
    </OnPremCollapseSection>
  );
}
